import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router';
import type { App, Plugin, Component } from 'vue';

import { unref } from 'vue';
import { isObject } from '/@/utils/is';
import { intersection, isEqual } from 'lodash-es';

export const noop = () => {};

/**
 * @description:  Set ui mount node
 */
export function getPopupContainer(node?: HTMLElement): HTMLElement {
  return (node?.parentNode as HTMLElement) ?? document.body;
}

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = '';
  for (const key in obj) {
    parameters += key + '=' + encodeURIComponent(obj[key]) + '&';
  }
  console.log(parameters);
  parameters = parameters.replace(/&$/, '');
  return /\?$/.test(baseUrl) ? baseUrl + parameters : baseUrl.replace(/\/?$/, '?') + parameters;
}

export function deepMerge<T = any>(src: any = {}, target: any = {}): T {
  let key: string;
  for (key in target) {
    src[key] = isObject(src[key]) ? deepMerge(src[key], target[key]) : (src[key] = target[key]);
  }
  return src;
}

export function openWindow(
  url: string,
  opt?: { target?: TargetContext | string; noopener?: boolean; noreferrer?: boolean },
) {
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {};
  const feature: string[] = [];

  noopener && feature.push('noopener=yes');
  noreferrer && feature.push('noreferrer=yes');

  window.open(url, target, feature.join(','));
}

// dynamic use hook props
export function getDynamicProps<T extends object, U>(props: T): Partial<U> {
  const ret: Recordable = {};

  Object.keys(props).map((key) => {
    ret[key] = unref((props as Recordable)[key]);
  });

  return ret as Partial<U>;
}

export function getRawRoute(route: RouteLocationNormalized): RouteLocationNormalized {
  if (!route) return route;
  const { matched, ...opt } = route;
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  };
}

export const withInstall = <T extends Component>(component: T, alias?: string) => {
  const comp = component as any;
  comp.install = (app: App) => {
    app.component(comp.name || comp.displayName, component);
    if (alias) {
      app.config.globalProperties[alias] = component;
    }
  };
  return component as T & { install: Plugin['install'] };
};

export const numberPrecision = (num) => {
  return parseFloat(num.toPrecision(12));
};

// 返回数据的类型： [] => array; {} => object; 1 => number; 'sss' => string; true => boolean;
export const getType = (data) => {
  return Object.prototype.toString.call(data).slice(8, -1).toLocaleLowerCase();
};

// 两个对象取交集
export const getObjectIntersection = (obj1, obj2) => {
  const obj = {};
  if (getType(obj1) !== 'object' || getType(obj2) !== 'object') {
    return obj;
  }
  Object.keys(obj1).forEach((key) => {
    // 对象类型递归取交集
    // 非对象类型直接判断是否相同
    if (
      getType(obj1[key]) === 'object' &&
      getType(obj2[key]) === 'object' &&
      obj1[key] &&
      obj2[key]
    ) {
      obj[key] = getObjectIntersection(obj1[key], obj2[key]);
    } else if (isEqual(obj1[key], obj2[key])) {
      obj[key] = obj2[key];
    } else if (
      getType(obj1[key]) === 'array' &&
      getType(obj2[key]) === 'array' &&
      obj1[key] &&
      obj2[key] &&
      key == 'commentTypeIds'
    ) {
      // 数组类型处理,暂仅对 commentTypeIds 进行处理
      obj[key] = intersection(obj1[key], obj2[key]);
    }
  });
  return obj;
};

/** 从对象obj中提取模板temp中所有key对应的值(子集)
obj =  {a: 1, b: {c: 2, d: 3}}, temp = {a: undefined, b: {c: undefined}}; 
结果： {
    "a": 1,
    "b": {
        "c": 2
    }
} */
export const filterObjectFromTemp = (obj: object, temp: object) => {
  const newObj = {};
  Object.keys(temp).forEach((key) => {
    // 如果模板temp中有子对象，递归根据模板提取
    if (getType(temp[key]) === 'object' && getType(obj[key]) === 'object' && obj[key]) {
      newObj[key] = filterObjectFromTemp(obj[key], temp[key]);
    } else {
      newObj[key] = obj[key];
    }
  });
  return newObj;
};
